import type { IconName } from "@bundle/tokens";
import type { ReactNode } from "react";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";

export type SectionHeaderProps = {
  title: string;
  /** Before the title (icon.sm). */
  icon?: IconName;
  /** An emoji in the icon slot instead of `icon` (a sidebar folder's label). */
  emoji?: string;
  /** With `onToggle`: a disclosure chevron; the title becomes a button with `aria-expanded`. */
  collapsed?: boolean;
  onToggle?: () => void;
  /** Small trailing controls: IconButton(s) or a Menu ("+", "…"). */
  trailing?: ReactNode;
  /**
   * "compact" (default): the sidebar, 20 px; the trailing controls overhang the row and show on hover, keyboard focus
   * or while their menu is open (always on touch). "regular": settings / picker groups, 28 px, trailing always shown.
   */
  density?: "compact" | "regular";
  /** Heading level for assistive tech (default 2). */
  level?: 2 | 3 | 4;
  /** The header; its toggle is `<testID>-toggle`. */
  testID?: string;
};

const tags = { 2: "h2", 3: "h3", 4: "h4" } as const;

/** The label over a group of rows: sidebar sections, settings groups, picker sections. caption · semibold · text.tertiary. */
export function SectionHeader({ title, icon, emoji, collapsed, onToggle, trailing, density = "compact", level = 2, testID }: SectionHeaderProps) {
  const Heading = tags[level];
  const compact = density === "compact";
  const label = (
    <>
      {emoji !== undefined && emoji !== "" ? (
        <span className="b-section-header-emoji" aria-hidden="true">
          {emoji}
        </span>
      ) : icon === undefined ? null : (
        <Icon name={icon} size="icon.sm" />
      )}
      <span className="b-section-header-label">{title}</span>
    </>
  );
  return (
    <div className={cx("b-section-header", `b-section-header-${density}`, compact && "b-hover-group")} data-testid={testID}>
      <Heading className="b-section-header-title">
        {onToggle === undefined ? (
          label
        ) : (
          <button
            type="button"
            className="b-section-header-toggle"
            aria-expanded={collapsed !== true}
            onClick={onToggle}
            data-testid={testID === undefined ? undefined : `${testID}-toggle`}
          >
            <Icon name={collapsed === true ? "chevronRight" : "chevronDown"} size="icon.sm" />
            {label}
          </button>
        )}
      </Heading>
      {trailing === undefined ? null : <div className={cx("b-section-header-trailing", compact && "b-reveal")}>{trailing}</div>}
    </div>
  );
}
